'use strict';

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {GENERIC_ERROR_MESSAGE} from "@uw-it-sis/lib-react/lib/AppConstants";

import events from '@uw-it-sis/lib-react/lib/Events';
import PlanEvents from './PlanEvents';

import { action, flow, observable, configure, makeObservable } from "mobx";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
configure({enforceActions: 'always'});

import {getPlaceholderDescriptionFromCode} from "./GeneralPlaceHolderConstants";

/**
 * Store for plan item actions: add, update, remove.
 *
 * This store is a singleton, so it can only have transaction in flight at a time. The id of that item is stored
 * in actionPendingItem. Error messages come from the response body and are stored in errorMessage.
 */
class PlanItemStore {

    state = States.initial;

    constructor() {
        makeObservable(this, {
            state: observable,
            add: action,
            addPlaceHolder: action,
            update: action,
            updatePlaceHolder: action,
            changeType: action
        });

        this.baseUrl = 'api/plan/items';
        this.errorMessage;
        this.actionPendingItem;
        this.actionPendingText;
    }

    /**
     * Add a course or section plan item
     * If user is being advised by an adviser the adviserRegId is set in the UserDetails and it means that the course
     * being added is recommended
     *
     * @param courseCd Course or section code
     * @param atpId term id for the plan item
     * @param note optional note text
     * @param backup boolean flag indicating the plan item is a backup
     * @param isSection boolean flag indicating the plan item is for a course section
     */
    add(courseCd, atpId, note, backup, isSection) {
        this.state = States.pending;
        // Clear any lingering error messages.
        this.errorMessage = "";
        this.actionPendingItem = courseCd;
        this.actionPendingText = `Adding ${courseCd} to your plan...`;
        let recommended = false;
        let adviserRegId = "";

        if (appStore.isAdviser()) {
            recommended = true;
            adviserRegId = appStore.getAdviserId();
        }

        const postBody = {
            "code": courseCd,
            "termId": atpId,
            "note": note,
            "backup": backup,
            "recommended": recommended,
            "adviserRegId": adviserRegId
        };

        const eventObject = {
            code: courseCd,
            description: courseCd,
            isSection: isSection,
            atpId: atpId,
            eventType: "planItemAdded"
        };

        this._addPlanItem(postBody, eventObject);
    }

    /**
     * Add a placeholder plan item
     * If user is being advised by an adviser the adviserRegId is set in the UserDetails and it means that the placeholder
     * being added is recommended
     *
     * @param placeHolderCode code determining which type of place holder to add
     *      Can be a course placeholder "COM 2XX" or a keyed placeholder type
     *        examples of keyed form:  course.genedrequirement.aofk.nw_ind|uw.course.genedrequirement
     *          or uw.academicplan.placeholder.studyabroad|uw.academicplan.placeholder
     *          or uw.academicplan.placeholder.other|uw.academicplan.placeholder
     *
     * @param atpId term id for the plan item
     * @param note optional note text
     * @param backup boolean flag indicating the plan item is a backup
     * @param credit optional credit value
     * @param placeholderText optional text for 'other' type placeholders
     */
    addPlaceHolder(placeHolderCode, atpId, note, backup, credit, placeholderText) {
        this.state = States.pending;
        // Clear any lingering error messages.
        this.errorMessage = "";
        this.actionPendingItem = placeHolderCode;
        this.actionPendingText = "Adding placeholder to your plan...";
        let recommended = false;
        let adviserRegId = "";

        if (appStore.isAdviser()) {
            recommended = true;
            adviserRegId = appStore.getAdviserId();
        }
        const postBody = {
            "code": placeHolderCode,
            "termId": atpId,
            "note": note,
            "backup": backup,
            "credit": credit,
            "placeholderText": placeholderText,
            "recommended": recommended,
            "adviserRegId": adviserRegId
        };

        const eventObject = {
            code: placeHolderCode,
            description: getPlaceholderDescriptionFromCode(placeHolderCode),
            isSection: false,
            atpId: atpId,
            eventType: "planItemAdded"
        };

        this._addPlanItem(postBody, eventObject);
    }

    /**
     * Handler for adding a section or secondary activity (and its associated section) to a plan.
     *
     * @param postBody - object of the data to post
     * @param eventObject - object of the event data used in success/error events
     *
     */
    _addPlanItem = flow(function* (postBody, eventObject) {

        try {
            yield appStore.getHttpClient().doPost(this.baseUrl, postBody, 'addPlanItem');
            // Announce the plan item was added.
            events.emit(PlanEvents.planItemAdded, eventObject);

            // Set the new state if no exceptions were thrown.
            this.state = States.done;
        } catch(error) {
            switch(error) {
                case 401:
                    appStore.getExceptionUtils().handleException(error);
                    break;
                default:
                    this._handlePlanItemError(error, eventObject);
                    break;
            }
        }
    }.bind(this));

    /**
     * Getter for the error message which is set if a request fails.
     */
    getErrorMessage() {
        return this.errorMessage;
    }

    /**
     * Handle planItem add/remove/etc. errors
     */
    _handlePlanItemError(error, eventObject) {
        // Can't use exception utils here because we need more granular handling. See if this error
        // is one of ours. If not, use the generic message.
        if (error.exceptionCode) {
            this.errorMessage = error.message;
        } else {
            this.errorMessage = GENERIC_ERROR_MESSAGE
        }
        this.state = States.error;

        const errEventObject = {
            errMessage: this.errorMessage,
            ...eventObject
        };
        events.emit(PlanEvents.planItemError, errEventObject);
    }

    /**
     * Update a plan item
     * If the user is being advised by an adviser the adviserRegId is set in the UserDetails and it means that the planItem
     * being added is recommended
     * @param planItemId - the plan item id of item being updated
     * @param courseCode - course code
     * @param atpId - term to update this course
     * @param note - A note. optional
     * @param credit - number of credits for plan item
     * @param selectedCredit - number of credits entered in case of variable credit. optional
     * @param selectedGradingOptionId - S/NS or standard (A section is either 0 (graded or 'standard') or '5' (CR/NC).
     * A student can opt to change the grading for their registration to 4 (S/NS) if grading on the section is 0 (graded
     * or 'standard'). optional
     * @param addCode - optional
     */
    update(planItemId, courseCode, atpId, note, credit, selectedCredit, selectedGradingOptionId, addCode) {
        this.state = States.pending;
        // Clear any lingering error messages.
        this.errorMessage = "";
        this.actionPendingItem = planItemId;
        this.actionPendingText = `Updating ${courseCode} in your plan...`;
        let recommended = false;
        let adviserRegId = "";

        if (appStore.isAdviser()) {
            recommended = true;
            adviserRegId = appStore.getAdviserId();
        }

        const postBody = {
            code: courseCode,
            termId: atpId,
            credit: credit,
            recommended: recommended,
            adviserRegId: adviserRegId
        };

        // add optional props only if value is set
        ["addCode", "note", "selectedCredit", "selectedGradingOptionId"].map(prop => {
            let propValue = eval(prop);
            if (propValue) {
                postBody[prop] = propValue;
            }
        });

        const eventObject = {
            planItemId: planItemId,
            code: courseCode,
            description: (courseCode.includes("|") ? getPlaceholderDescriptionFromCode(courseCode) : courseCode),
            atpId: atpId,
            eventType: "planItemUpdated"
        };

        this._updatePlanItem(planItemId, postBody, eventObject);
    }

    /**
     * Update a plan item placeholder
     * If the user is being advised by an adviser the adviserRegId is set in the UserDetails and it means that the planItem
     * being added is recommended
     * @param planItemId - the plan item id of item being updated
     * @param placeHolderCode - the code for the placeholder to be updated
     * @param atpId - term to update this placeholder
     * @param note - A note. optional
     * @param backup - boolean whether the item is a backup item or not
     * @param credit - number of credits for plan item
     * @param placeholderText - The custom name of the placeholder if the code is "other"
     */
    updatePlaceHolder(planItemId, placeHolderCode, atpId, note, backup, credit, placeholderText) {
        this.state = States.pending;
        // Clear any lingering error messages.
        this.errorMessage = "";
        this.actionPendingItem = planItemId;
        this.actionPendingText = `Updating ${placeHolderCode} in your plan...`;
        let recommended = false;
        let adviserRegId = "";

        if (appStore.isAdviser()) {
            recommended = true;
            adviserRegId = appStore.getAdviserId();
        }
        const postBody = {
            code: placeHolderCode,
            termId: atpId,
            note: note,
            credit: credit,
            backup: backup,
            placeholderText: placeholderText,
            recommended: recommended,
            adviserRegId: adviserRegId
        };

        const eventObject = {
            planItemId: planItemId,
            code: placeHolderCode,
            description: getPlaceholderDescriptionFromCode(placeHolderCode),
            atpId: atpId,
            eventType: "planItemUpdated"
        };

        this._updatePlanItem(planItemId, postBody, eventObject);
    }
    
    /**
     * Handler for updating information on a plan item.
     *
     * @param planItemId - the id of the plan item
     * @param postBody - object of the data to post
     * @param eventObject - object of the event data used in success/error events
     */
    _updatePlanItem = flow(function* (planItemId, postBody, eventObject) {
        const updateUrl = this.baseUrl + "/" + planItemId;

        try {
            yield appStore.getHttpClient().doPut(updateUrl, postBody,'updatePlanItem');

            events.emit(PlanEvents[eventObject.eventType], eventObject);
            this.state = States.done;
        } catch(error) {
            switch(error) {
                case 401:
                    appStore.getExceptionUtils().handleException(error);
                    this.state = States.done;
                    break;
                default:
                    this._handlePlanItemError(error, eventObject);
                    break;
            }
        }
    }.bind(this));

    /**
     * Handler for removing a section or secondary activity from a plan.
     *
     * @param planItemId - ID of item to remove from plan
     *
     */
    remove = flow(function* (planItemId) {
        this.state = States.pending;
        this.errorMessage = "";
        this.actionPendingItem = planItemId;
        this.actionPendingText = `Removing ${planItemId} from your plan...`;

        const eventObject = {
            planItemId: planItemId,
            eventType: "planItemRemoved"
        };

        let url = `${this.baseUrl}/${planItemId}`;

        try {
            yield appStore.getHttpClient().doDelete(url, 'removePlanItem');
            events.emit(PlanEvents.planItemRemoved, eventObject);
            this.state = States.done;
        } catch (error) {
            switch(error) {
                case 401:
                    appStore.getExceptionUtils().handleException(error);
                    this.state = States.done;
                    break;
                default:
                    this._handlePlanItemError(error, eventObject);
                    break;
            }
        }
    }.bind(this));

    /**
     * Handler for changing the type of plan item (e.g. from planned to backup)
     *
     * @param planItemId - the plan item id of item being updated
     * @param type - the new type of the plan item (e.g. 'backup', 'recommended', or 'planned')
     * @param code - plan item code for event messaging ( course code, placeholder type, etc )
     * @param note - Any existing note on the plan item
     * @param placeholderText - Any existing custom placeholder text
     * @param credit - number of credits for plan item
     *
     */
    changeType(planItemId, type, code, note, placeholderText, credit) {
        this.state = States.pending;
        this.errorMessage = "";
        this.actionPendingItem = planItemId;
        this.actionPendingText = `Changing ${planItemId} to ${type}...`;

        let serviceType = `kuali.academicplan.item.${encodeURIComponent(type)}`;

        const eventObject = {
            planItemId: planItemId,
            code: code,
            description: code,
            newType: type,
            eventType: "planItemTypeChanged"
        };

        const postBody = {
            type: serviceType,
            note: note,
            placeholderText: placeholderText,
            credit: credit
        };

        this._updatePlanItem(planItemId, postBody, eventObject);
    }

    /**
     * Handler for accepting an adviser recommended plan item.
     *
     * @param planItemId - the id of the recommended plan item
     */
    acceptRecommendation = flow(function* (planItemId) {
        this.state = States.pending;
        this.errorMessage = "";
        this.actionPendingItem = planItemId;
        this.actionPendingText = `Adding recommended ${planItemId} to your plan...`;

        const eventObject = {
            planItemId: planItemId,
            eventType: "recommendedPlanItemAccepted"
        };

        const recommendedUrl = `${this.baseUrl}/${planItemId}`;
        const patchBody = {
            recommended: true
        };
        try {
            yield appStore.getHttpClient().doPatch(recommendedUrl, patchBody,'acceptRecommendation');

            events.emit(PlanEvents.recommendedPlanItemAccepted, eventObject);
            this.state = States.done;
        } catch(error) {

            /*
             * If the exception is a service exception and has a code of UNKNOWN_ENTITY, the recommended item
             * being accepted was already removed by an adviser.
             * If that happens, show a more helpful error message.
             */
            if (error.exceptionCode && error.exceptionCode === 'UNKNOWN_ENTITY') {

                let craftedError = {
                    exceptionCode: error.exceptionCode,
                    message: 'Unable to accept the recommendation. It may have been removed by an adviser. Try refreshing the page.'
                }

                this._handlePlanItemError(craftedError, eventObject);
            }
            else {
                switch(error) {
                    case 401:
                        appStore.getExceptionUtils().handleException(error);
                        this.state = States.done;
                        break;
                    default:
                        this._handlePlanItemError(error, eventObject);
                        break;
                }
            }
        }
    }.bind(this));
}

export default new PlanItemStore();